import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export const DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'kanban_panel' },
  { name: 'jornada_360' },
  { name: 'tasks_panel' },
  // conversation_actions: pinned as three boxes in ContactInfo (no AccordionItem).
  { name: 'macros' },
  { name: 'conversation_info' },
  // SiteUp (CT-C): origem do lead — campanha desta conversa + primeira vez.
  { name: 'lead_origin' },
  { name: 'contact_attributes' },
  // Lead Scoring — aba/accordion gated pela feature flag lead_scoring.
  { name: 'lead_scoring' },
  { name: 'contact_notes' },
  { name: 'shared_files' },
  { name: 'previous_conversation' },
  { name: 'conversation_participants' },
  { name: 'linear_issues' },
  { name: 'shopify_orders' },
]);

// SiteUp: seções ruidosas ocultas até o agente ligar no customizador.
// Participantes/Time/Etiquetas ficam pinned no card Contatos (três caixas
// diretas, sem shell "Ações da conversa"); o item sidebar não entra na ordem.
export const DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS = Object.freeze([
  'lead_scoring',
  'contact_notes',
  'shared_files',
  'previous_conversation',
  'conversation_participants',
  'linear_issues',
  'shopify_orders',
  // kanban_panel: intentionally NOT hidden by default (Contatos menu).
]);

export const DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'contact_attributes' },
  { name: 'contact_labels' },
  { name: 'previous_conversation' },
]);

/**
 * Slugifies the channel name.
 * Replaces spaces, hyphens, and double colons with underscores.
 * @param {string} name - The channel name to slugify.
 * @returns {string} The slugified channel name.
 */
const slugifyChannel = name =>
  name?.toLowerCase().replace(' ', '_').replace('-', '_').replace('::', '_');

/**
 * Computes the order of items in the conversation sidebar, using defaults if not present.
 * Jornada 360 is only included when FEATURE_FLAGS.JORNADA_360 is enabled on the account,
 * so ContactPanel sidebar/customizer never list or mount the panel when the flag is off.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {import('vue').ComputedRef<boolean>} isJornada360Enabled
 * @returns {Array} Ordered list of sidebar items.
 */
const useConversationSidebarItemsOrder = (uiSettings, isJornada360Enabled) => {
  return computed(() => {
    const { conversation_sidebar_items_order: itemsOrder } = uiSettings.value;
    let itemsOrderCopy;
    // If the sidebar order is not set, use the default order.
    if (!itemsOrder) {
      itemsOrderCopy = [...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER];
    } else {
      // Preserve the saved order and only insert newly introduced sections.
      itemsOrderCopy = [...itemsOrder];
      if (!itemsOrderCopy.some(item => item.name === 'kanban_panel')) {
        itemsOrderCopy.unshift({ name: 'kanban_panel' });
      }
      if (!itemsOrderCopy.some(item => item.name === 'jornada_360')) {
        const kanbanIndex = itemsOrderCopy.findIndex(
          item => item.name === 'kanban_panel'
        );
        itemsOrderCopy.splice(kanbanIndex + 1, 0, { name: 'jornada_360' });
      }
      // If the sidebar order doesn't have the new elements, then add them to the list.
      DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER.forEach(item => {
        if (!itemsOrderCopy.find(i => i.name === item.name)) {
          itemsOrderCopy.push(item);
        }
      });
    }

    if (!isJornada360Enabled.value) {
      itemsOrderCopy = itemsOrderCopy.filter(
        item => item.name !== 'jornada_360'
      );
    }
    // SiteUp: conversation_actions vive pinned em ContactInfo (três caixas),
    // nunca como AccordionItem "Ações da conversa" no sidebar.
    return itemsOrderCopy.filter(item => item.name !== 'conversation_actions');
  });
};

/**
 * Computes the order of items in the contact sidebar,using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useContactSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { contact_sidebar_items_order: itemsOrder } = uiSettings.value;
    return itemsOrder || DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER;
  });
};

/**
 * Toggles the open state of a sidebar item.
 * @param {string} key - The key of the sidebar item to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleSidebarUIState = (key, uiSettings, updateUISettings) => {
  updateUISettings({ [key]: !uiSettings.value[key] });
};

/**
 * Sets the signature flag for a specific channel type in the inbox settings.
 * @param {string} channelType - The type of the channel.
 * @param {boolean} value - The value to set for the signature enabled flag.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const setSignatureFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_signature_enabled`]: value });
};

const setQuotedReplyFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_quoted_reply_enabled`]: value });
};

/**
 * Fetches the signature flag for a specific channel type from UI settings.
 * @param {string} channelType - The type of the channel.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The value of the signature enabled flag.
 */
const fetchSignatureFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_signature_enabled`];
};

const fetchQuotedReplyFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_quoted_reply_enabled`];
};

/**
 * Checks if a specific editor hotkey is enabled.
 * @param {string} key - The key to check.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} True if the hotkey is enabled, otherwise false.
 */
const isEditorHotKeyEnabled = (key, uiSettings) => {
  const {
    editor_message_key: editorMessageKey,
    enter_to_send_enabled: enterToSendEnabled,
  } = uiSettings.value || {};
  if (!editorMessageKey) {
    return key === (enterToSendEnabled ? 'enter' : 'cmd_enter');
  }
  return editorMessageKey === key;
};

/**
 * Main composable function for managing UI settings.
 * @returns {Object} An object containing reactive properties and methods for UI settings management.
 */
export function useUISettings() {
  const getters = useStoreGetters();
  const store = useStore();
  const uiSettings = computed(() => {
    const settings = getters.getUISettings.value || {};
    // Inject SiteUp default-hidden sections when the user has never customized.
    if (!Array.isArray(settings.conversation_sidebar_hidden_items)) {
      return {
        ...settings,
        conversation_sidebar_hidden_items: [
          ...DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS,
        ],
      };
    }
    // SiteUp: Kanban must stay in the default-visible Contatos sections.
    // Strip accidental hides (e.g. after conversation_actions accordion removal).
    if (settings.conversation_sidebar_hidden_items.includes('kanban_panel')) {
      return {
        ...settings,
        conversation_sidebar_hidden_items:
          settings.conversation_sidebar_hidden_items.filter(
            name => name !== 'kanban_panel'
          ),
      };
    }
    return settings;
  });
  const currentAccountId = computed(() => getters.getCurrentAccountId?.value);
  const isFeatureEnabledonAccount = computed(
    () => getters['accounts/isFeatureEnabledonAccount']?.value
  );
  // Fail closed: without account/getter, keep Jornada out of the sidebar order.
  const isJornada360Enabled = computed(() => {
    const accountId = currentAccountId.value;
    const isEnabled = isFeatureEnabledonAccount.value;
    if (!accountId || typeof isEnabled !== 'function') return false;
    return !!isEnabled(accountId, FEATURE_FLAGS.JORNADA_360);
  });

  const updateUISettings = (settings = {}) => {
    // SiteUp: envia SÓ o delta — a mutation local já faz o merge e o servidor
    // agora mescla por chave (ProfilesController). Enviar o objeto inteiro fazia
    // um cliente stale (aba antiga/celular) clobberar as preferências salvas por
    // outro dispositivo a cada gravação.
    store.dispatch('updateUISettings', {
      uiSettings: { ...settings },
    });
  };

  return {
    uiSettings,
    updateUISettings,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(
      uiSettings,
      isJornada360Enabled
    ),
    contactSidebarItemsOrder: useContactSidebarItemsOrder(uiSettings),
    isContactSidebarItemOpen: key => !!uiSettings.value[key],
    toggleSidebarUIState: key =>
      toggleSidebarUIState(key, uiSettings, updateUISettings),
    setSignatureFlagForInbox: (channelType, value) =>
      setSignatureFlagForInbox(channelType, value, updateUISettings),
    fetchSignatureFlagFromUISettings: channelType =>
      fetchSignatureFlagFromUISettings(channelType, uiSettings),
    setQuotedReplyFlagForInbox: (channelType, value) =>
      setQuotedReplyFlagForInbox(channelType, value, updateUISettings),
    fetchQuotedReplyFlagFromUISettings: channelType =>
      fetchQuotedReplyFlagFromUISettings(channelType, uiSettings),
    isEditorHotKeyEnabled: key => isEditorHotKeyEnabled(key, uiSettings),
  };
}
